import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBell, FaUsers, FaDollarSign, FaStar, FaClock, FaCheck, FaCalendar } from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '../../utils/constants';
import { getGreetingMessage } from '../../utils/utils';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './Dashboard.css';

const Dashboard = () => {
  const { user, setUser } = useUser();
  const [dashboardData, setDashboardData] = useState({
    totalAppointments: 0,
    totalPatients: 0,
    totalRevenue: 0,
    overallRating: 0,
    totalUpcomingAppointments: 0,
    totalCompletedAppointments: 0,
  });
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    fetchDashboardData();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchProfile();
    await fetchDashboardData();
    setRefreshing(false);
  };

  const checklistItems = [
    {
      label: "Manage Your Specializations",
      completed: user?.speciality && user?.speciality?.length > 0,
      onPress: () => navigate('/manage-services'),
    },
    {
      label: "Set Opening Hours",
      completed: user?.availableSlots && user?.availableSlots?.length !== 0,
      onPress: () => console.log('Navigate to Opening Hours'),
    },
    {
      label: "Update Consultation Fees",
      completed: user?.services && user?.services?.length > 0,
      onPress: () => console.log('Navigate to Consultation Fees'),
    },
    {
      label: "Edit About, Education, and Experience",
      completed: user?.education && user?.education?.length > 0,
      onPress: () => console.log('Navigate to Edit Profile'),
    },
    {
      label: "Update Profile Picture",
      completed: user && user.profilePicture,
      onPress: () => console.log('Navigate to Update Profile Picture'),
    },
    {
      label: "Location",
      description: "Add your clinic or practice location to help clients find you easily.",
      completed: user && user.location && user.location.city,
      onPress: () => console.log('Navigate to Update Location'),
    },
  ];

  const completionPercentage = (checklistItems.filter((item) => item.completed).length / checklistItems.length) * 100;

  const fetchProfile = async () => {
    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      if (accessToken) {
        const response = await fetch(`${API_BASE_URL}api/doctor/profile`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          const formattedData = data.doctor;
          setUser({
            ...user,
            name: formattedData.fullName,
            email: formattedData.email,
            mobile: formattedData.mobile,
            services: formattedData.services || [],
            speciality: formattedData.speciality || [],
            about: formattedData.about,
            education: formattedData.education || [],
            experience: formattedData.experience || [],
            profilePicture: formattedData.profilePicture || null,
            availableSlots: formattedData.availableSlots || [],
            location: formattedData.address || null,
          });
        } else {
          console.error('Failed to fetch profile data');
        }
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_BASE_URL}api/doctor/dashboard`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        console.error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };



  const renderContent = () => (
    <div className="home-content">
      <div className="welcome-banner">
        <h2>Welcome back, {user?.name?.split(' ')[0]}! 👋</h2>
        <p>Here's a quick overview of your recent activities and stats.</p>
      </div>

      {!loading && completionPercentage < 100 && (
        <div className="pending-tasks-container" onClick={() => navigate('/complete-profile')} style={{ cursor: 'pointer' }}>
          <div className="pending-tasks-header">
            <FaBell className="pending-tasks-icon" />
            <h3>Complete Your Profile</h3>
          </div>
          <p className="pending-tasks-description">
            You have pending tasks to complete your profile. Completing your profile helps clients trust you more.
          </p>
          <div className="progress-bar-wrapper">
            <div className="progress-bar" style={{ width: `${completionPercentage}%` }}></div>
          </div>
          <p className="pending-tasks-progress">
            Your profile is {Math.round(completionPercentage)}% complete. Tap to finish!
          </p>
        </div>
      )}

      <div className="stats-section">
        <h3 className="stats-title">Dashboard</h3>
        <div className="stats-grid">
          <div className="stat-card" onClick={() => navigate('/past-appointments')}>
            <FaCalendar className="stat-icon" />
            <div className="stat-info">
              <h3>{dashboardData.totalAppointments}</h3>
              <p>Total Appointments</p>
            </div>
          </div>

          <div className="stat-card">
            <FaUsers className="stat-icon" />
            <div className="stat-info">
              <h3>{dashboardData.totalPatients}</h3>
              <p>Total Patients</p>
            </div>
          </div>

          <div className="stat-card" onClick={() => navigate('/earnings')}>
            <FaDollarSign className="stat-icon" />
            <div className="stat-info">
              <h3>₹{dashboardData.totalRevenue}</h3>
              <p>Total Revenue</p>
            </div>
          </div>

          <div className="stat-card" onClick={() => navigate('/reviews')}>
            <FaStar className="stat-icon" />
            <div className="stat-info">
              <h3>{dashboardData.overallRating || 'N/A'}</h3>
              <p>Overall Rating</p>
            </div>
          </div>

          <div className="stat-card" onClick={() => navigate('/appointments')}>
            <FaClock className="stat-icon" />
            <div className="stat-info">
              <h3>{dashboardData.totalUpcomingAppointments}</h3>
              <p>Upcoming Appointments</p>
            </div>
          </div>

          <div className="stat-card" onClick={() => navigate('/past-appointments')}>
            <FaCheck className="stat-icon" />
            <div className="stat-info">
              <h3>{dashboardData.totalCompletedAppointments}</h3>
              <p>Completed Appointments</p>
            </div>
          </div>
        </div>

        <button className="view-stats-button" onClick={() => navigate('/complete-profile')}>
          Manage Your Accounts
        </button>
      </div>
    </div>
  );







  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  return (
    <DashboardLayout title="Dashboard">
      {renderContent()}
    </DashboardLayout>
  );
};

export default Dashboard;